// src/pages/NewsDetailPage.tsx
// Public news article view — root domain

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import { useSeo } from '../hooks/useSeo';
import Navbar from '../components/layout/Navbar';
import '../styles/news-detail.css';

const SUBDOMAIN_BASE = process.env.REACT_APP_SUBDOMAIN_BASE || '.playsalt.gg';
const PROTOCOL = window.location.protocol;
const ARENA_URL = `${PROTOCOL}//hi${SUBDOMAIN_BASE}`;

interface NewsPost {
  post_id: number;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  featured_image_url: string | null;
  categories: string[];
  tags: string[];
  author_username: string;
  published_at: string;
  meta_title: string | null;
  meta_description: string | null;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const NewsDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<NewsPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const articleDescription =
    post?.meta_description || post?.excerpt || post?.content.replace(/\s+/g, ' ').trim().slice(0, 155);
  useSeo({
    title: post ? post.meta_title || post.title : notFound ? 'Post not found' : undefined,
    description: articleDescription || undefined,
    image: post?.featured_image_url || undefined,
    type: post ? 'article' : undefined,
    noindex: notFound,
    jsonLd: post
      ? {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: post.title,
          description: articleDescription,
          image: post.featured_image_url || undefined,
          datePublished: post.published_at,
          author: { '@type': 'Person', name: post.author_username },
          publisher: { '@type': 'Organization', name: 'SALT', url: 'https://playsalt.gg' },
          mainEntityOfPage: `https://playsalt.gg/news/${post.slug}`,
        }
      : undefined,
  });

  useEffect(() => {
    if (!slug) return;
    axiosClient
      .get(`/news/${slug}`)
      .then((res) => setPost(res.data.data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="sl-nd">
      <Navbar />

      <main className="sl-nd-main">
        {loading && (
          <div className="sl-nd-loading">
            <div className="sl-nd-loading-bar" />
            <div className="sl-nd-loading-bar sl-nd-loading-bar--short" />
            <div className="sl-nd-loading-bar sl-nd-loading-bar--med" />
          </div>
        )}

        {notFound && (
          <div className="sl-nd-notfound">
            <h1>Post Not Found</h1>
            <p>This article doesn't exist or may have been removed.</p>
            <button onClick={() => navigate(-1)} className="sl-nd-back-btn">← Go Back</button>
          </div>
        )}

        {post && (
          <article className="sl-nd-article">
            {/* Breadcrumb */}
            <nav className="sl-nd-breadcrumb">
              <a href="/">Home</a>
              <span>›</span>
              <a href="/news">News</a>
              <span>›</span>
              <span>{post.title}</span>
            </nav>

            {/* Category */}
            {post.categories?.length > 0 && (
              <div className="sl-nd-categories">
                {post.categories.map((cat) => (
                  <span key={cat} className="sl-nd-category">{cat}</span>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="sl-nd-title">{post.title}</h1>

            {/* Meta */}
            <div className="sl-nd-meta">
              <span>By {post.author_username}</span>
              <span className="sl-nd-meta-dot">·</span>
              <span>{formatDate(post.published_at)}</span>
            </div>

            {/* Featured image */}
            {post.featured_image_url && (
              <div className="sl-nd-hero-img">
                <img src={post.featured_image_url} alt={post.title} />
              </div>
            )}

            {/* Content */}
            <div className="sl-nd-content">
              {post.content.split('\n').map((line, i) =>
                line.trim() === '' ? (
                  <br key={i} />
                ) : (
                  <p key={i}>{line}</p>
                )
              )}
            </div>

            {/* Tags */}
            {post.tags?.length > 0 && (
              <div className="sl-nd-tags">
                {post.tags.map((tag) => (
                  <span key={tag} className="sl-nd-tag">#{tag}</span>
                ))}
              </div>
            )}

            {/* Back link */}
            <div className="sl-nd-footer-nav">
              <a href="/news" className="sl-nd-back-btn">← Back to News</a>
            </div>
          </article>
        )}
      </main>

      <footer className="sl-nd-footer">
        &copy; {new Date().getFullYear()} SALT &middot;{' '}
        <a href={`${ARENA_URL}/auth`}>Sign In</a> &middot;{' '}
        <a href="/legal">Legal</a>
      </footer>
    </div>
  );
};

export default NewsDetailPage;
